import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Users, Activity, CreditCard, DollarSign, ShieldCheck, Clock,
  CheckCircle2, XCircle, Loader2, Search, Eye, RefreshCw,
  Settings, AlertTriangle, ArrowUpRight, ArrowDownRight,
  Wallet, FileText, CircleDollarSign, TrendingUp, Copy, Save,
  Ban, UserCheck, ChevronRight, Banknote, BarChart3, Gift, Coins, RefreshCcw, Zap
} from 'lucide-react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from 'recharts';
import { CopyableText } from '@/components/CopyButton';

const STATUS_COLORS = {
  pending: 'bg-[rgba(247,200,75,0.1)] text-[hsl(43,90%,60%)] border-[hsl(43,90%,60%)]/20',
  reviewing: 'bg-[rgba(55,240,196,0.1)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20',
  approved: 'bg-[rgba(45,211,111,0.1)] text-[#2DD36F] border-[#2DD36F]/20',
  rejected: 'bg-[rgba(217,74,74,0.1)] text-[#D94A4A] border-[#D94A4A]/20',
  successful: 'bg-[rgba(45,211,111,0.1)] text-[#2DD36F] border-[#2DD36F]/20',
  failed: 'bg-[rgba(217,74,74,0.1)] text-[#D94A4A] border-[#D94A4A]/20',
  processing: 'bg-[rgba(40,170,255,0.1)] text-[#28AAFF] border-[#28AAFF]/20',
};

const CHART_COLORS = {
  successful: '#2DD36F',
  failed: '#D94A4A',
  pending: '#F7C84B',
  processing: '#28AAFF',
};

export default function AdminPage() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [kycQueue, setKycQueue] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [paymentLogsTotal, setPaymentLogsTotal] = useState(0);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [activityFeed, setActivityFeed] = useState([]);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [balanceForm, setBalanceForm] = useState({ balance_type: 'demo', asset: 'usdt', amount: '', note: '' });
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState('pending');
  const [withdrawStatus, setWithdrawStatus] = useState('pending');
  const [deposits, setDeposits] = useState([]);
  const [depositsTotal, setDepositsTotal] = useState(0);
  const [depositFilter, setDepositFilter] = useState('all');
  const [depositCheckLoading, setDepositCheckLoading] = useState(false);

  // SafeCoin Admin State
  const [safecoinStats, setSafecoinStats] = useState(null);
  const [safecoinLeaderboard, setSafecoinLeaderboard] = useState([]);
  const [safecoinGrantDialog, setSafecoinGrantDialog] = useState(false);
  const [safecoinGrantForm, setSafecoinGrantForm] = useState({ user_id: '', amount: '', reason: '' });
  const [safecoinGrantLoading, setSafecoinGrantLoading] = useState(false);
  const [safecoinGrantMode, setSafecoinGrantMode] = useState('grant'); // grant or revoke

  // Coin Management State
  const [adminCoins, setAdminCoins] = useState([]);
  const [coinEditDialog, setCoinEditDialog] = useState(false);
  const [editingCoin, setEditingCoin] = useState(null);
  const [coinSaving, setCoinSaving] = useState(false);

  // GameFuel State
  const [gamefuelConfig, setGamefuelConfig] = useState(null);
  const [gamefuelStats, setGamefuelStats] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const res = await adminAPI.getStats();
      setStats(res.data);
    } catch (err) {
      console.error('Stats error:', err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await adminAPI.getUsers({ search: userSearch });
      setUsers(res.data.users || []);
      setUsersTotal(res.data.total || 0);
    } catch (err) {
      console.error('Users error:', err);
    }
  }, [userSearch]);

  const loadKYC = useCallback(async () => {
    try {
      const res = await adminAPI.getKYCQueue({ status: kycStatus });
      setKycQueue(res.data.cases || []);
    } catch (err) {
      console.error('KYC error:', err);
    }
  }, [kycStatus]);

  const loadWithdrawals = useCallback(async () => {
    try {
      const res = await adminAPI.getWithdrawals({ status: withdrawStatus });
      setWithdrawals(res.data.withdrawals || []);
    } catch (err) {
      console.error('Withdrawals error:', err);
    }
  }, [withdrawStatus]);

  const loadPaymentLogs = useCallback(async () => {
    try {
      const res = await adminAPI.getPaymentLogs({ status: paymentFilter });
      setPaymentLogs(res.data.logs || []);
      setPaymentLogsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Payments error:', err);
    }
  }, [paymentFilter]);

  const loadActivity = useCallback(async () => {
    try {
      const res = await adminAPI.getActivityFeed({});
      setActivityFeed(res.data.activities || []);
    } catch (err) {
      console.error('Activity error:', err);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await adminAPI.getPlatformSettings();
      setPlatformSettings(res.data.settings);
      setSettingsForm(res.data.settings);
    } catch (err) {
      console.error('Settings error:', err);
    }
  }, []);

  const loadDeposits = useCallback(async () => {
    try {
      const res = await adminAPI.getDeposits({ status: depositFilter });
      setDeposits(res.data.deposits || []);
      setDepositsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Deposits error:', err);
    }
  }, [depositFilter]);

  const loadSafecoin = useCallback(async () => {
    try {
      const [statsRes, lbRes] = await Promise.all([
        adminAPI.getSafecoinStats(),
        adminAPI.getSafecoinLeaderboard(),
      ]);
      setSafecoinStats(statsRes.data);
      setSafecoinLeaderboard(lbRes.data.leaderboard || []);
    } catch (err) {
      console.error('SafeCoin error:', err);
    }
  }, []);

  const loadAdminCoins = useCallback(async () => {
    try {
      const res = await adminAPI.getSupportedCoins();
      setAdminCoins(res.data.coins || []);
    } catch (err) {
      console.error('Coins error:', err);
    }
  }, []);

  const loadGamefuel = useCallback(async () => {
    try {
      const [configRes, statsRes] = await Promise.all([
        adminAPI.getGamefuelConfig(),
        adminAPI.getGamefuelStats(),
      ]);
      setGamefuelConfig(configRes.data);
      setGamefuelStats(statsRes.data);
    } catch (err) {
      console.error('GameFuel error:', err);
    }
  }, []);

  const saveGamefuelConfig = async () => {
    try {
      await adminAPI.updateGamefuelConfig(gamefuelConfig);
      toast.success('GameFuel configuration saved');
      loadGamefuel();
    } catch (err) {
      toast.error('Failed to save configuration');
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadActivity(), loadSettings()]);
      setLoading(false);
    };
    loadAll();
  }, [loadStats, loadActivity, loadSettings]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadKYC(); }, [loadKYC]);
  useEffect(() => { loadWithdrawals(); }, [loadWithdrawals]);
  useEffect(() => { loadPaymentLogs(); }, [loadPaymentLogs]);
  useEffect(() => { loadDeposits(); }, [loadDeposits]);

  const handleKYCAction = async (kycId, action) => {
    try {
      await adminAPI.kycAction(kycId, { action, notes: '' });
      toast.success(action === 'approve' ? t('admin.kycApproved') : t('admin.kycRejected'));
      await loadKYC();
      await loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const handleWithdrawAction = async (id, action) => {
    try {
      await adminAPI.withdrawAction(id, { action });
      toast.success(t('admin.withdrawActioned'));
      await loadWithdrawals();
      await loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const handleDepositAction = async (depositId, action) => {
    try {
      await adminAPI.depositAction(depositId, { action, note: '' });
      toast.success(action === 'approve' ? t('admin.approveDeposit') : t('admin.rejectDeposit'));
      await loadDeposits();
      await loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const handleTriggerDepositCheck = async () => {
    setDepositCheckLoading(true);
    try {
      const res = await adminAPI.triggerDepositCheck();
      toast.success(`${res.data.checked} ${t('admin.depositsChecked')}, ${res.data.confirmed} ${t('admin.depositsConfirmed')}`);
      await loadDeposits();
      await loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setDepositCheckLoading(false);
    }
  };

  const handleSafecoinGrant = async () => {
    setSafecoinGrantLoading(true);
    try {
      const payload = { user_id: safecoinGrantForm.user_id, amount: parseInt(safecoinGrantForm.amount), reason: safecoinGrantForm.reason };
      if (safecoinGrantMode === 'grant') {
        await adminAPI.grantSafecoin(payload);
        toast.success(`${payload.amount} SafeCoin verildi`);
      } else {
        await adminAPI.revokeSafecoin(payload);
        toast.success(`${payload.amount} SafeCoin geri alindi`);
      }
      setSafecoinGrantDialog(false);
      setSafecoinGrantForm({ user_id: '', amount: '', reason: '' });
      await loadSafecoin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    } finally {
      setSafecoinGrantLoading(false);
    }
  };

  const handleDistributeLeaderboard = async () => {
    try {
      const res = await adminAPI.distributeLeaderboardBonuses();
      toast.success(res.data.message);
      await loadSafecoin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    }
  };

  const handleCoinToggle = async (index) => {
    const updated = [...adminCoins];
    updated[index] = { ...updated[index], enabled: !updated[index].enabled };
    setAdminCoins(updated);
    try {
      await adminAPI.updateSupportedCoins({ coins: updated });
      toast.success(`${updated[index].symbol} ${updated[index].enabled ? 'aktif' : 'deaktif'} edildi`);
    } catch (err) {
      toast.error('Coin durumu guncellenemedi');
    }
  };

  const handleCoinSave = async () => {
    if (!editingCoin) return;
    setCoinSaving(true);
    try {
      const updated = adminCoins.map(c =>
        c.symbol === editingCoin.symbol ? editingCoin : c
      );
      await adminAPI.updateSupportedCoins({ coins: updated });
      setAdminCoins(updated);
      setCoinEditDialog(false);
      toast.success(`${editingCoin.symbol} guncellendi`);
    } catch (err) {
      toast.error('Kayit hatasi');
    } finally {
      setCoinSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      const updateData = {};
      Object.keys(settingsForm).forEach(key => {
        if (key !== 'key' && key !== 'updated_at' && key !== 'updated_by' && settingsForm[key] !== platformSettings[key]) {
          updateData[key] = settingsForm[key];
        }
      });
      if (Object.keys(updateData).length === 0) {
        toast.info(t('admin.noChanges'));
        return;
      }
      await adminAPI.updatePlatformSettings(updateData);
      toast.success(t('admin.settingsSaved'));
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const openUserDetail = async (userId) => {
    setSelectedUser(userId);
    setUserDetailLoading(true);
    try {
      const res = await adminAPI.getUserDetail(userId);
      setUserDetail(res.data);
    } catch (err) {
      toast.error(t('common.error'));
    } finally {
      setUserDetailLoading(false);
    }
  };

  const handleBalanceUpdate = async () => {
    if (!balanceForm.amount || !selectedUser) return;
    setBalanceLoading(true);
    try {
      await adminAPI.updateUserBalance(selectedUser, {
        balance_type: balanceForm.balance_type,
        asset: balanceForm.asset,
        amount: parseFloat(balanceForm.amount),
        note: balanceForm.note,
      });
      toast.success(t('admin.balanceUpdated'));
      setBalanceDialog(false);
      setBalanceForm({ balance_type: 'demo', asset: 'usdt', amount: '', note: '' });
      await openUserDetail(selectedUser);
      await loadUsers();
      await loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setBalanceLoading(false);
    }
  };

  const formatMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const formatAddr = (a) => a ? `${a.slice(0, 10)}...${a.slice(-6)}` : '-';

  const chartData = stats ? [
    { name: t('admin.paySuccessful'), value: stats.payment_stats.successful, color: CHART_COLORS.successful },
    { name: t('admin.payFailed'), value: stats.payment_stats.failed, color: CHART_COLORS.failed },
    { name: t('admin.payPending'), value: stats.payment_stats.pending, color: CHART_COLORS.pending },
    { name: t('admin.payProcessing'), value: stats.payment_stats.processing, color: CHART_COLORS.processing },
  ] : [];

  return (
    <DashboardLayout title={t('admin.title')}>
      <div className="max-w-7xl space-y-6">
        <Tabs defaultValue="overview">
          <TabsList className="bg-[hsl(var(--secondary))] flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview" data-testid="admin-overview-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm">
              <BarChart3 className="w-4 h-4" /> {t('admin.overview')}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-users-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm" onClick={() => loadUsers()}>
              <Users className="w-4 h-4" /> {t('admin.usersTab')}
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="admin-payments-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm" onClick={() => loadPaymentLogs()}>
              <FileText className="w-4 h-4" /> {t('admin.paymentsTab')}
            </TabsTrigger>
            <TabsTrigger value="kyc" data-testid="admin-kyc-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm" onClick={() => loadKYC()}>
              <ShieldCheck className="w-4 h-4" /> KYC
            </TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="admin-withdrawals-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm" onClick={() => loadWithdrawals()}>
              <Banknote className="w-4 h-4" /> {t('admin.withdrawalsTab')}
            </TabsTrigger>
            <TabsTrigger value="deposits" data-testid="admin-deposits-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm" onClick={() => loadDeposits()}>
              <ArrowDownRight className="w-4 h-4" /> {t('admin.depositsTab')}
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-settings-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm" onClick={() => loadSettings()}>
              <Settings className="w-4 h-4" /> {t('admin.settingsTab')}
            </TabsTrigger>
            <TabsTrigger value="safecoin" data-testid="admin-safecoin-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm" onClick={() => loadSafecoin()}>
              <Coins className="w-4 h-4" /> SafeCoin
            </TabsTrigger>
            <TabsTrigger value="gamefuel" data-testid="admin-gamefuel-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm" onClick={() => loadGamefuel()}>
              <Zap className="w-4 h-4" /> GameFuel
            </TabsTrigger>
          </TabsList>

          {/* ============ OVERVIEW TAB ============ */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {loading ? [1,2,3,4].map(i => <Card key={i} className="bg-[hsl(var(--card))] border-[hsl(var(--border))]"><CardContent className="p-4"><Skeleton className="h-16" /></CardContent></Card>) : (
                <>
                  <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="admin-stat-users">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-center justify-between mb-2">
                        <Users className="w-5 h-5 text-[hsl(var(--primary))]" />
                        <Badge className="text-[10px] bg-[rgba(45,211,111,0.1)] text-[#2DD36F] border-[#2DD36F]/20">+{stats?.new_users_7d || 0}</Badge>
                      </div>
                      <p className="text-2xl font-bold font-['Space_Grotesk']">{stats?.total_users || 0}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('admin.totalUsers')}</p>
                      <p className="text-xs text-[hsl(var(--primary))] mt-1">{stats?.active_users || 0} {t('admin.activeUsers')}</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="admin-stat-demo">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-center justify-between mb-2">
                        <Gift className="w-5 h-5 text-[hsl(43,90%,60%)]" />
                      </div>
                      <p className="text-2xl font-bold font-['Space_Grotesk']">{formatMoney(stats?.total_demo_balance)}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('admin.totalDemoBalance')}</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="admin-stat-real">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-center justify-between mb-2">
                        <CircleDollarSign className="w-5 h-5 text-[#2DD36F]" />
                      </div>
                      <p className="text-2xl font-bold font-['Space_Grotesk']">{formatMoney(stats?.total_real_balance)}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('admin.totalRealBalance')}</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="admin-stat-pending">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-center justify-between mb-2">
                        <AlertTriangle className="w-5 h-5 text-[hsl(43,90%,60%)]" />
                      </div>
                      <p className="text-2xl font-bold font-['Space_Grotesk']">{(stats?.pending_kyc || 0) + (stats?.pending_withdrawals || 0) + (stats?.pending_card_apps || 0)}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('admin.pendingActions')}</p>
                      <div className="flex gap-2 mt-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                        <span>KYC: {stats?.pending_kyc || 0}</span>
                        <span>{t('admin.withdrawals')}: {stats?.pending_withdrawals || 0}</span>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Chart + Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Payment Chart */}
              <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="admin-payment-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-['Space_Grotesk'] flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[hsl(var(--primary))]" />
                    {t('admin.paymentOverview')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {chartData.length > 0 && chartData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                        <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {chartData.map((entry, idx) => (
                            <Cell key={idx} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[200px] text-sm text-[hsl(var(--muted-foreground))]">
                      {t('admin.noPaymentData')}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Activity Feed */}
              <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="admin-activity-feed">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-['Space_Grotesk'] flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[hsl(var(--primary))]" />
                      {t('admin.recentActivity')}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={loadActivity} className="h-7 w-7 p-0">
                      <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[240px] overflow-y-auto">
                    {activityFeed.length === 0 ? (
                      <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-6">{t('admin.noActivity')}</p>
                    ) : (
                      activityFeed.slice(0, 10).map((act, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-white/[0.02] transition-colors">
                          <div className="w-8 h-8 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center flex-shrink-0">
                            {act.type === 'bonus' ? <Gift className="w-4 h-4 text-[hsl(43,90%,60%)]" /> :
                             act.type === 'kyc_submission' ? <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" /> :
                             <ArrowUpRight className="w-4 h-4 text-[hsl(var(--primary))]" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{act.description}</p>
                            <p className="text-[10px] text-[hsl(var(--muted-foreground))]"><CopyableText value={act.user_email} mono={false} /> - {formatDate(act.timestamp)}</p>
                          </div>
                          {act.amount > 0 && (
                            <span className="text-xs font-mono text-[#2DD36F] flex-shrink-0">+{act.amount} {act.asset?.toUpperCase()}</span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ============ USERS TAB ============ */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                <Input
                  data-testid="admin-user-search"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder={t('admin.searchUsers')}
                  className="pl-9 bg-[hsl(var(--background))] border-[hsl(var(--border))]"
                />
              </div>
              <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]">{usersTotal} {t('admin.usersCount')}</Badge>
            </div>

            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[hsl(var(--border))]/60 hover:bg-transparent">
                      <TableHead className="text-xs">{t('admin.userEmail')}</TableHead>
                      <TableHead className="text-xs">{t('admin.userName')}</TableHead>
                      <TableHead className="text-xs text-right">{t('admin.demoBalance')}</TableHead>
                      <TableHead className="text-xs text-right">{t('admin.realBalance')}</TableHead>
                      <TableHead className="text-xs">KYC</TableHead>
                      <TableHead className="text-xs">{t('admin.registered')}</TableHead>
                      <TableHead className="text-xs w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-[hsl(var(--border))]/40 cursor-pointer hover:bg-white/[0.02]" onClick={() => openUserDetail(u.id)}>
                        <TableCell className="text-xs"><CopyableText value={u.email} /></TableCell>
                        <TableCell className="text-xs">{u.name || '-'}</TableCell>
                        <TableCell className="text-xs text-right font-mono text-[hsl(43,90%,60%)]">{formatMoney(u.demo_balance)}</TableCell>
                        <TableCell className="text-xs text-right font-mono text-[#2DD36F]">{formatMoney(u.real_balance)}</TableCell>
                        <TableCell><Badge className={`text-[10px] ${STATUS_COLORS[u.kyc_status] || STATUS_COLORS.pending}`}>{u.kyc_status || 'none'}</Badge></TableCell>
                        <TableCell className="text-xs text-[hsl(var(--muted-foreground))]">{formatDate(u.created_at)}</TableCell>
                        <TableCell><ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* ============ PAYMENTS TAB ============ */}
          <TabsContent value="payments" className="space-y-4">
            <div className="flex items-center gap-3">
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger className="w-40 bg-[hsl(var(--background))] border-[hsl(var(--border))]" data-testid="admin-payment-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <SelectItem value="all">{t('admin.allPayments')}</SelectItem>
                  <SelectItem value="successful">{t('admin.paySuccessful')}</SelectItem>
                  <SelectItem value="failed">{t('admin.payFailed')}</SelectItem>
                  <SelectItem value="pending">{t('admin.payPending')}</SelectItem>
                  <SelectItem value="processing">{t('admin.payProcessing')}</SelectItem>
                </SelectContent>
              </Select>
              <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]">{paymentLogsTotal} {t('admin.records')}</Badge>
            </div>

            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[hsl(var(--border))]/60 hover:bg-transparent">
                      <TableHead className="text-xs">{t('admin.date')}</TableHead>
                      <TableHead className="text-xs">{t('admin.type')}</TableHead>
                      <TableHead className="text-xs">{t('admin.balanceType')}</TableHead>
                      <TableHead className="text-xs text-right">{t('admin.amount')}</TableHead>
                      <TableHead className="text-xs">{t('admin.status')}</TableHead>
                      <TableHead className="text-xs">{t('admin.note')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentLogs.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-[hsl(var(--muted-foreground))] py-8">{t('admin.noPaymentLogs')}</TableCell></TableRow>
                    ) : (
                      paymentLogs.map((log) => (
                        <TableRow key={log.id} className="border-[hsl(var(--border))]/40">
                          <TableCell className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{formatDate(log.created_at)}</TableCell>
                          <TableCell className="text-xs">{log.type}</TableCell>
                          <TableCell><Badge className={`text-[10px] ${log.balance_type === 'demo' ? 'bg-[rgba(247,200,75,0.1)] text-[hsl(43,90%,60%)]' : 'bg-[rgba(45,211,111,0.1)] text-[#2DD36F]'}`}>{log.balance_type || '-'}</Badge></TableCell>
                          <TableCell className={`text-xs text-right font-mono ${log.amount >= 0 ? 'text-[#2DD36F]' : 'text-[#D94A4A]'}`}>{log.amount >= 0 ? '+' : ''}{log.amount} {log.asset?.toUpperCase()}</TableCell>
                          <TableCell><Badge className={`text-[10px] ${STATUS_COLORS[log.status] || ''}`}>{log.status}</Badge></TableCell>
                          <TableCell className="text-xs text-[hsl(var(--muted-foreground))] max-w-[200px] truncate">{log.note || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* ============ KYC TAB ============ */}
          <TabsContent value="kyc" className="space-y-4">
            <div className="flex items-center gap-3">
              <Select value={kycStatus} onValueChange={setKycStatus}>
                <SelectTrigger className="w-40 bg-[hsl(var(--background))] border-[hsl(var(--border))]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <SelectItem value="pending">{t('admin.payPending')}</SelectItem>
                  <SelectItem value="approved">{t('admin.paySuccessful')}</SelectItem>
                  <SelectItem value="rejected">{t('admin.payFailed')}</SelectItem>
                  <SelectItem value="all">{t('admin.allPayments')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[hsl(var(--border))]/60 hover:bg-transparent">
                      <TableHead className="text-xs">{t('admin.userName')}</TableHead>
                      <TableHead className="text-xs">{t('admin.docType')}</TableHead>
                      <TableHead className="text-xs">{t('admin.status')}</TableHead>
                      <TableHead className="text-xs">{t('admin.date')}</TableHead>
                      <TableHead className="text-xs">{t('admin.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kycQueue.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-sm text-[hsl(var(--muted-foreground))] py-8">{t('admin.noKycCases')}</TableCell></TableRow>
                    ) : (
                      kycQueue.map((kyc) => (
                        <TableRow key={kyc.id} className="border-[hsl(var(--border))]/40">
                          <TableCell className="text-xs font-medium">{kyc.full_name}</TableCell>
                          <TableCell className="text-xs">{kyc.document_type}</TableCell>
                          <TableCell><Badge className={`text-[10px] ${STATUS_COLORS[kyc.status] || ''}`}>{kyc.status}</Badge></TableCell>
                          <TableCell className="text-xs text-[hsl(var(--muted-foreground))]">{formatDate(kyc.submitted_at)}</TableCell>
                          <TableCell className="flex gap-2">
                            {kyc.status === 'pending' && (
                              <>
                                <Button size="sm" className="h-7 text-xs bg-[#2DD36F] hover:bg-[#2DD36F]/90 text-white" onClick={() => handleKYCAction(kyc.id, 'approve')}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> {t('admin.approve')}
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs border-[#D94A4A]/30 text-[#D94A4A] hover:bg-[rgba(217,74,74,0.08)]" onClick={() => handleKYCAction(kyc.id, 'reject')}>
                                  <XCircle className="w-3 h-3 mr-1" /> {t('admin.reject')}
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* ============ WITHDRAWALS TAB ============ */}
          <TabsContent value="withdrawals" className="space-y-4">
            <div className="flex items-center gap-3">
              <Select value={withdrawStatus} onValueChange={setWithdrawStatus}>
                <SelectTrigger className="w-40 bg-[hsl(var(--background))] border-[hsl(var(--border))]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <SelectItem value="pending">{t('admin.payPending')}</SelectItem>
                  <SelectItem value="approved">{t('admin.paySuccessful')}</SelectItem>
                  <SelectItem value="rejected">{t('admin.payFailed')}</SelectItem>
                  <SelectItem value="all">{t('admin.allPayments')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[hsl(var(--border))]/60 hover:bg-transparent">
                      <TableHead className="text-xs">{t('admin.userEmail')}</TableHead>
                      <TableHead className="text-xs text-right">{t('admin.amount')}</TableHead>
                      <TableHead className="text-xs">{t('admin.toAddress')}</TableHead>
                      <TableHead className="text-xs">{t('admin.status')}</TableHead>
                      <TableHead className="text-xs">{t('admin.date')}</TableHead>
                      <TableHead className="text-xs">{t('admin.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {withdrawals.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-[hsl(var(--muted-foreground))] py-8">{t('admin.noWithdrawals')}</TableCell></TableRow>
                    ) : (
                      withdrawals.map((w) => (
                        <TableRow key={w.id} className="border-[hsl(var(--border))]/40">
                          <TableCell className="text-xs"><CopyableText value={w.user_email || w.user_id?.slice(0, 8)} /></TableCell>
                          <TableCell className="text-xs text-right font-mono">{w.amount} {w.asset?.toUpperCase()}</TableCell>
                          <TableCell className="text-xs"><CopyableText value={w.to_address ? formatAddr(w.to_address) : '-'} /></TableCell>
                          <TableCell><Badge className={`text-[10px] ${STATUS_COLORS[w.status] || ''}`}>{w.status}</Badge></TableCell>
                          <TableCell className="text-xs text-[hsl(var(--muted-foreground))]">{formatDate(w.created_at)}</TableCell>
                          <TableCell className="flex gap-2">
                            {w.status === 'pending' && (
                              <>
                                <Button size="sm" className="h-7 text-xs bg-[#2DD36F] hover:bg-[#2DD36F]/90 text-white" onClick={() => handleWithdrawAction(w.id, 'approve')}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> {t('admin.approve')}
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs border-[#D94A4A]/30 text-[#D94A4A] hover:bg-[rgba(217,74,74,0.08)]" onClick={() => handleWithdrawAction(w.id, 'reject')}>
                                  <XCircle className="w-3 h-3 mr-1" /> {t('admin.reject')}
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* ============ DEPOSITS TAB ============ */}
          <TabsContent value="deposits" className="space-y-4">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="font-['Space_Grotesk'] text-lg">{t('admin.depositsTab')}</CardTitle>
                  <div className="flex items-center gap-3">
                    <Select value={depositFilter} onValueChange={setDepositFilter}>
                      <SelectTrigger data-testid="admin-deposit-filter" className="w-[140px] bg-[hsl(var(--background))] border-[hsl(var(--border))] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                        <SelectItem value="all">{t('admin.allPayments')}</SelectItem>
                        <SelectItem value="pending">{t('admin.payPending')}</SelectItem>
                        <SelectItem value="confirmed">{t('wallet.depositConfirmed')}</SelectItem>
                        <SelectItem value="unmatched">{t('admin.unmatchedDeposits')}</SelectItem>
                        <SelectItem value="support_review">{t('admin.supportTicketsTab')}</SelectItem>
                        <SelectItem value="failed">{t('admin.payFailed')}</SelectItem>
                        <SelectItem value="rejected">{t('common.rejected')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      data-testid="admin-trigger-deposit-check"
                      disabled={depositCheckLoading}
                      onClick={handleTriggerDepositCheck}
                      className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-8 text-xs gap-1.5"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${depositCheckLoading ? 'animate-spin' : ''}`} />
                      {t('admin.triggerCheck')}
                    </Button>
                  </div>
                </div>
                <CardDescription>{depositsTotal} {t('admin.records')}</CardDescription>
              </CardHeader>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[hsl(var(--border))]/40">
                      <TableHead className="text-xs">{t('admin.depositUser')}</TableHead>
                      <TableHead className="text-xs">{t('admin.depositTxHash')}</TableHead>
                      <TableHead className="text-xs text-right">{t('admin.depositDeclared')}</TableHead>
                      <TableHead className="text-xs text-right">{t('admin.depositVerified')}</TableHead>
                      <TableHead className="text-xs">From</TableHead>
                      <TableHead className="text-xs">{t('admin.depositStatus')}</TableHead>
                      <TableHead className="text-xs">{t('admin.depositDate')}</TableHead>
                      <TableHead className="text-xs">{t('admin.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deposits.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]">{t('admin.noDeposits')}</TableCell></TableRow>
                    ) : (
                      deposits.map((d) => (
                        <TableRow key={d.id} data-testid={`admin-deposit-row-${d.id}`} className="border-[hsl(var(--border))]/40">
                          <TableCell className="text-xs">
                            <CopyableText value={d.user_email_display || d.user_email || d.user_id?.slice(0, 8)} />
                          </TableCell>
                          <TableCell className="text-xs">
                            <CopyableText value={d.tx_hash ? `${d.tx_hash.slice(0, 10)}...${d.tx_hash.slice(-6)}` : '-'} fullValue={d.tx_hash} />
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono">
                            {d.declared_amount} {d.declared_asset?.toUpperCase()}
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono">
                            {d.verified_amount != null ? `${d.verified_amount} ${(d.verified_asset || '').toUpperCase()}` : '-'}
                          </TableCell>
                          <TableCell className="text-xs">
                            {d.from_address ? <CopyableText value={`${d.from_address.slice(0, 8)}...${d.from_address.slice(-6)}`} fullValue={d.from_address} /> : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-[10px] ${STATUS_COLORS[d.status] || STATUS_COLORS.pending}`}>
                              {d.status === 'confirmed' && <CheckCircle2 className="w-3 h-3 mr-0.5" />}
                              {d.status === 'pending' && <Clock className="w-3 h-3 mr-0.5" />}
                              {d.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-[hsl(var(--muted-foreground))]">{formatDate(d.created_at)}</TableCell>
                          <TableCell className="flex gap-2">
                            {(d.status === 'pending' || d.status === 'unmatched' || d.status === 'support_review') && (
                              <>
                                <Button size="sm" className="h-7 text-xs bg-[#2DD36F] hover:bg-[#2DD36F]/90 text-white" onClick={() => handleDepositAction(d.id, 'approve')}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> {t('admin.approve')}
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs border-[#D94A4A]/30 text-[#D94A4A] hover:bg-[rgba(217,74,74,0.08)]" onClick={() => handleDepositAction(d.id, 'reject')}>
                                  <XCircle className="w-3 h-3 mr-1" /> {t('admin.reject')}
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* ============ SETTINGS TAB ============ */}
          <TabsContent value="settings" className="space-y-4">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="admin-platform-settings">
              <CardHeader>
                <CardTitle className="font-['Space_Grotesk'] flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-[hsl(var(--primary))]" />
                  {t('admin.platformWallet')}
                </CardTitle>
                <CardDescription className="text-[hsl(var(--muted-foreground))]">
                  {t('admin.platformWalletDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs">{t('admin.walletAddress')}</Label>
                  <Input
                    data-testid="admin-platform-wallet-input"
                    value={settingsForm.platform_wallet_address || ''}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, platform_wallet_address: e.target.value }))}
                    placeholder="TRX Cüzdan Adresi (ör: TXxxxxxx...)"
                    className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">{t('admin.walletNetwork')}</Label>
                  <Select value={settingsForm.platform_wallet_network || 'tron'} onValueChange={(v) => setSettingsForm(prev => ({ ...prev, platform_wallet_network: v }))}>
                    <SelectTrigger className="bg-[hsl(var(--background))] border-[hsl(var(--border))]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="tron">TRON (TRC-20)</SelectItem>
                      <SelectItem value="ethereum">Ethereum (ERC-20)</SelectItem>
                      <SelectItem value="bsc">BSC (BEP-20)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="font-['Space_Grotesk'] flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[hsl(var(--primary))]" />
                  {t('admin.financialSettings')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">{t('admin.welcomeBonus')} (USDT)</Label>
                    <Input
                      type="number"
                      value={settingsForm.welcome_bonus_usdt || ''}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, welcome_bonus_usdt: parseFloat(e.target.value) || 0 }))}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{t('admin.minTrxDeposit')} (TRX)</Label>
                    <Input
                      type="number"
                      value={settingsForm.min_trx_deposit || ''}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, min_trx_deposit: parseFloat(e.target.value) || 0 }))}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{t('admin.referralInviterBonus')} (USDT)</Label>
                    <Input
                      type="number"
                      value={settingsForm.referral_inviter_bonus || ''}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, referral_inviter_bonus: parseFloat(e.target.value) || 0 }))}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{t('admin.referralInvitedBonus')} (USDT)</Label>
                    <Input
                      type="number"
                      value={settingsForm.referral_invited_bonus || ''}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, referral_invited_bonus: parseFloat(e.target.value) || 0 }))}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono"
                    />
                  </div>
                </div>
                <Separator className="bg-[hsl(var(--border))]/40" />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{t('admin.maintenanceMode')}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('admin.maintenanceModeDesc')}</p>
                  </div>
                  <Switch
                    data-testid="admin-maintenance-toggle"
                    checked={settingsForm.maintenance_mode || false}
                    onCheckedChange={(checked) => setSettingsForm(prev => ({ ...prev, maintenance_mode: checked }))}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Coin Management */}
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-['Space_Grotesk'] flex items-center gap-2">
                    <Coins className="w-5 h-5 text-yellow-500" />
                    Desteklenen Coinler
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={loadAdminCoins} className="gap-1 text-xs">
                    <RefreshCcw className="w-3 h-3" /> Yukle
                  </Button>
                </div>
                <CardDescription className="text-[hsl(var(--muted-foreground))]">
                  Yatirma icin desteklenen coinleri yonetin. Coin'i aktif/deaktif edin ve cuzdan adresi girin.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {adminCoins.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">Coinleri yuklemek icin "Yukle" butonuna tiklayin</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">Aktif</TableHead>
                        <TableHead>Coin</TableHead>
                        <TableHead>Ag</TableHead>
                        <TableHead>Min. Yatirma</TableHead>
                        <TableHead>Adres</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adminCoins.map((coin, idx) => (
                        <TableRow key={coin.symbol}>
                          <TableCell>
                            <Switch
                              checked={coin.enabled}
                              onCheckedChange={() => handleCoinToggle(idx)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{coin.symbol} <span className="text-xs text-[hsl(var(--muted-foreground))]">({coin.name})</span></TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{coin.network}</Badge></TableCell>
                          <TableCell className="font-mono text-sm">{coin.min_deposit}</TableCell>
                          <TableCell className="font-mono text-xs max-w-[150px] truncate">{coin.address || '-'}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingCoin({ ...coin }); setCoinEditDialog(true); }}>
                              <Settings className="w-3 h-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="sticky bottom-4 flex justify-end">
              <Button
                data-testid="admin-save-settings"
                onClick={handleSaveSettings}
                disabled={settingsSaving}
                className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2 shadow-lg"
              >
                {settingsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('admin.saveSettings')}
              </Button>
            </div>
          </TabsContent>

          {/* ============ SAFECOIN TAB ============ */}
          <TabsContent value="safecoin" className="space-y-4">
            {/* SafeCoin Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-[hsl(var(--card))] border-yellow-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                      <Coins className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Dolasimdaki SC</p>
                      <p className="text-xl font-bold text-yellow-500">{safecoinStats?.total_in_circulation || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[hsl(var(--card))] border-green-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Toplam Verilen</p>
                      <p className="text-xl font-bold text-green-400">{safecoinStats?.total_issued || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center">
                      <Users className="w-5 h-5 text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">SC Sahipleri</p>
                      <p className="text-xl font-bold">{safecoinStats?.total_holders || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Tamamlanan Gorev</p>
                      <p className="text-xl font-bold">{safecoinStats?.total_missions_completed || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Mission Breakdown */}
            {safecoinStats?.mission_breakdown && (
              <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-sm">Gorev Dagilimi</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(safecoinStats.mission_breakdown).map(([key, count]) => (
                      <div key={key} className="p-3 rounded-lg bg-[hsl(var(--secondary))] text-center">
                        <p className="text-lg font-bold">{count}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{key.replace('_', ' ')}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Button data-testid="admin-sc-grant-btn" size="sm" className="bg-green-600 hover:bg-green-700 gap-1" onClick={() => { setSafecoinGrantMode('grant'); setSafecoinGrantDialog(true); }}>
                <Gift className="w-4 h-4" /> SafeCoin Ver
              </Button>
              <Button data-testid="admin-sc-revoke-btn" size="sm" variant="destructive" className="gap-1" onClick={() => { setSafecoinGrantMode('revoke'); setSafecoinGrantDialog(true); }}>
                <Ban className="w-4 h-4" /> SafeCoin Geri Al
              </Button>
              <Button data-testid="admin-sc-distribute-btn" size="sm" variant="outline" className="gap-1 border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10" onClick={handleDistributeLeaderboard}>
                <BarChart3 className="w-4 h-4" /> Liderlik Bonusu Dagit
              </Button>
            </div>

            {/* Leaderboard Table */}
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-yellow-500" /> SafeCoin Liderlik Tablosu</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Kullanici</TableHead>
                      <TableHead>E-posta</TableHead>
                      <TableHead className="text-right">Bakiye</TableHead>
                      <TableHead className="text-right">Kazanilan</TableHead>
                      <TableHead className="text-right">Bonus</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {safecoinLeaderboard.map((entry) => (
                      <TableRow key={entry.user_id}>
                        <TableCell className="font-bold">{entry.rank}</TableCell>
                        <TableCell className="font-medium">{entry.name}</TableCell>
                        <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">{entry.email}</TableCell>
                        <TableCell className="text-right font-semibold text-yellow-500">{entry.balance}</TableCell>
                        <TableCell className="text-right text-green-400">{entry.total_earned}</TableCell>
                        <TableCell className="text-right">{entry.bonus > 0 ? <Badge className="bg-green-500/10 text-green-400">+{entry.bonus}</Badge> : '-'}</TableCell>
                      </TableRow>
                    ))}
                    {safecoinLeaderboard.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-[hsl(var(--muted-foreground))] py-6">Henuz SafeCoin verisi yok</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Recent SafeCoin Transactions */}
            {safecoinStats?.recent_transactions?.length > 0 && (
              <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-sm">Son SafeCoin Islemleri</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Kullanici</TableHead>
                        <TableHead>Tur</TableHead>
                        <TableHead>Aciklama</TableHead>
                        <TableHead className="text-right">Miktar</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {safecoinStats.recent_transactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="text-xs text-[hsl(var(--muted-foreground))]">{new Date(tx.created_at).toLocaleDateString('tr-TR')}</TableCell>
                          <TableCell className="text-sm">{tx.user_email || tx.user_name}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{tx.entry_type}</Badge></TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate">{tx.description}</TableCell>
                          <TableCell className={`text-right font-semibold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>{tx.amount > 0 ? '+' : ''}{tx.amount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ============ GAMEFUEL TAB ============ */}
          <TabsContent value="gamefuel" className="space-y-4">
            {/* Stats Row */}
            {gamefuelStats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <CardContent className="p-4">
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Total Granted</p>
                    <p className="text-xl font-bold font-mono text-[#F7931A]">${gamefuelStats.total_granted_usd}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <CardContent className="p-4">
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Active Balance</p>
                    <p className="text-xl font-bold font-mono text-[#2DD36F]">${gamefuelStats.total_remaining_usd}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <CardContent className="p-4">
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Card Purchases</p>
                    <p className="text-xl font-bold font-mono">${gamefuelStats.total_card_purchases_usd}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <CardContent className="p-4">
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Unique Users</p>
                    <p className="text-xl font-bold font-mono">{gamefuelStats.unique_users}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Campaign Config */}
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Space_Grotesk']">
                  <Zap className="w-5 h-5 text-[#F7931A]" /> GameFuel Campaign Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Campaign Status</Label>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Enable or disable the GameFuel campaign</p>
                  </div>
                  <Switch
                    data-testid="gamefuel-toggle"
                    checked={gamefuelConfig?.enabled || false}
                    onCheckedChange={(v) => setGamefuelConfig(prev => ({ ...prev, enabled: v }))}
                  />
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Credit Percentage (%)</Label>
                    <Input
                      data-testid="gamefuel-credit-pct"
                      type="number"
                      min="1" max="100"
                      value={gamefuelConfig?.credit_percentage || 100}
                      onChange={e => setGamefuelConfig(prev => ({ ...prev, credit_percentage: parseInt(e.target.value) }))}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Expiry Days</Label>
                    <Input
                      data-testid="gamefuel-expiry-days"
                      type="number"
                      min="1" max="365"
                      value={gamefuelConfig?.expiry_days || 30}
                      onChange={e => setGamefuelConfig(prev => ({ ...prev, expiry_days: parseInt(e.target.value) }))}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max Uses / Month</Label>
                    <Input
                      data-testid="gamefuel-max-uses"
                      type="number"
                      min="1" max="100"
                      value={gamefuelConfig?.max_uses_per_month || 5}
                      onChange={e => setGamefuelConfig(prev => ({ ...prev, max_uses_per_month: parseInt(e.target.value) }))}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Monthly Cap (USD)</Label>
                    <Input
                      data-testid="gamefuel-monthly-cap"
                      type="number"
                      min="10" max="100000"
                      value={gamefuelConfig?.monthly_cap_usd || 500}
                      onChange={e => setGamefuelConfig(prev => ({ ...prev, monthly_cap_usd: parseInt(e.target.value) }))}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] mt-1"
                    />
                  </div>
                </div>
                <Button
                  data-testid="gamefuel-save-btn"
                  onClick={saveGamefuelConfig}
                  className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] gap-2"
                >
                  <Save className="w-4 h-4" /> Save Configuration
                </Button>
              </CardContent>
            </Card>

            {/* Stats Detail */}
            {gamefuelStats && (
              <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="font-['Space_Grotesk'] text-base">Campaign Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-[hsl(var(--muted-foreground))] text-xs">Credits Issued</p>
                      <p className="font-semibold">{gamefuelStats.total_credits_issued}</p>
                    </div>
                    <div>
                      <p className="text-[hsl(var(--muted-foreground))] text-xs">Active Credits</p>
                      <p className="font-semibold text-[#2DD36F]">{gamefuelStats.active_credits}</p>
                    </div>
                    <div>
                      <p className="text-[hsl(var(--muted-foreground))] text-xs">Total Used</p>
                      <p className="font-semibold">${gamefuelStats.total_used_usd}</p>
                    </div>
                    <div>
                      <p className="text-[hsl(var(--muted-foreground))] text-xs">Flagged</p>
                      <p className={`font-semibold ${gamefuelStats.flagged_credits > 0 ? 'text-[#D94A4A]' : ''}`}>
                        {gamefuelStats.flagged_credits}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* SafeCoin Grant/Revoke Dialog */}
      <Dialog open={safecoinGrantDialog} onOpenChange={setSafecoinGrantDialog}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <DialogHeader>
            <DialogTitle>{safecoinGrantMode === 'grant' ? 'SafeCoin Ver' : 'SafeCoin Geri Al'}</DialogTitle>
            <DialogDescription>Kullaniciya {safecoinGrantMode === 'grant' ? 'SafeCoin verin' : 'SafeCoin geri alin'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Kullanici ID</Label>
              <Input
                data-testid="admin-sc-user-id"
                value={safecoinGrantForm.user_id}
                onChange={(e) => setSafecoinGrantForm(prev => ({ ...prev, user_id: e.target.value }))}
                placeholder="Kullanici UUID"
                className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Miktar (SC)</Label>
              <Input
                data-testid="admin-sc-amount"
                type="number"
                value={safecoinGrantForm.amount}
                onChange={(e) => setSafecoinGrantForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0"
                className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">Sebep</Label>
              <Input
                data-testid="admin-sc-reason"
                value={safecoinGrantForm.reason}
                onChange={(e) => setSafecoinGrantForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Sebep (opsiyonel)"
                className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSafecoinGrantDialog(false)}>Iptal</Button>
            <Button
              data-testid="admin-sc-submit"
              disabled={safecoinGrantLoading || !safecoinGrantForm.user_id || !safecoinGrantForm.amount}
              onClick={handleSafecoinGrant}
              className={safecoinGrantMode === 'grant' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {safecoinGrantLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {safecoinGrantMode === 'grant' ? 'Ver' : 'Geri Al'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail Sheet */}
      <Sheet open={!!selectedUser && !!userDetail} onOpenChange={(open) => { if (!open) { setSelectedUser(null); setUserDetail(null); } }}>
        <SheetContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-full sm:max-w-lg overflow-y-auto">
          {userDetailLoading ? (
            <div className="space-y-4 pt-6">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-32" />
            </div>
          ) : userDetail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-['Space_Grotesk']">{userDetail.user?.name || userDetail.user?.email}</SheetTitle>
                <SheetDescription className="text-[hsl(var(--muted-foreground))]">
                  <CopyableText value={userDetail.user?.email} />
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-5 mt-6">
                {/* Balance Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-[rgba(247,200,75,0.05)] border border-[hsl(43,90%,60%)]/10">
                    <p className="text-[10px] uppercase tracking-wider text-[hsl(43,90%,60%)] mb-1">{t('admin.demoBalance')}</p>
                    <p className="text-lg font-bold font-mono">{formatMoney(userDetail.wallet?.demo_usdt_balance || userDetail.wallet?.usdt_balance || 0)}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-[rgba(45,211,111,0.05)] border border-[#2DD36F]/10">
                    <p className="text-[10px] uppercase tracking-wider text-[#2DD36F] mb-1">{t('admin.realBalance')}</p>
                    <p className="text-lg font-bold font-mono">{formatMoney(userDetail.wallet?.real_usdt_balance || 0)}</p>
                  </div>
                </div>

                <Button
                  data-testid="admin-user-balance-edit"
                  onClick={() => setBalanceDialog(true)}
                  className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2"
                >
                  <CircleDollarSign className="w-4 h-4" /> {t('admin.editBalance')}
                </Button>

                {/* User Info */}
                <div className="space-y-2">
                  <div className="flex justify-between p-2.5 rounded-lg bg-[hsl(var(--secondary))]">
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('admin.status')}</span>
                    <Badge className={userDetail.user?.is_active ? STATUS_COLORS.approved : STATUS_COLORS.rejected}>{userDetail.user?.is_active ? t('common.active') : t('admin.inactive')}</Badge>
                  </div>
                  <div className="flex justify-between p-2.5 rounded-lg bg-[hsl(var(--secondary))]">
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">KYC</span>
                    <Badge className={STATUS_COLORS[userDetail.user?.kyc_status] || STATUS_COLORS.pending}>{userDetail.user?.kyc_status || 'none'}</Badge>
                  </div>
                  <div className="flex justify-between p-2.5 rounded-lg bg-[hsl(var(--secondary))]">
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">2FA</span>
                    <Badge className={userDetail.user?.two_fa_enabled ? STATUS_COLORS.approved : STATUS_COLORS.pending}>{userDetail.user?.two_fa_enabled ? t('admin.enabled') : t('admin.disabled')}</Badge>
                  </div>
                  <div className="flex justify-between p-2.5 rounded-lg bg-[hsl(var(--secondary))]">
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('admin.referralCode')}</span>
                    {userDetail.user?.referral_code ? <CopyableText value={userDetail.user.referral_code} /> : <span className="text-xs font-mono">-</span>}
                  </div>
                  <div className="flex justify-between p-2.5 rounded-lg bg-[hsl(var(--secondary))]">
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('admin.registered')}</span>
                    <span className="text-xs font-mono">{formatDate(userDetail.user?.created_at)}</span>
                  </div>
                </div>

                {/* Recent Transactions */}
                {userDetail.transactions?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">{t('admin.recentTransactions')}</h4>
                    <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                      {userDetail.transactions.slice(0, 10).map((tx, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-[hsl(var(--secondary))] text-xs">
                          <span className="truncate max-w-[180px] text-[hsl(var(--muted-foreground))]">{tx.description}</span>
                          <span className={`font-mono ${tx.amount >= 0 ? 'text-[#2DD36F]' : 'text-[#D94A4A]'}`}>{tx.amount >= 0 ? '+' : ''}{tx.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Balance Edit Dialog */}
      <Dialog open={balanceDialog} onOpenChange={setBalanceDialog}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] flex items-center gap-2">
              <CircleDollarSign className="w-5 h-5 text-[hsl(var(--primary))]" />
              {t('admin.editBalance')}
            </DialogTitle>
            <DialogDescription className="text-[hsl(var(--muted-foreground))]">
              {t('admin.editBalanceDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('admin.balanceType')}</Label>
                <Select value={balanceForm.balance_type} onValueChange={(v) => setBalanceForm(prev => ({ ...prev, balance_type: v }))}>
                  <SelectTrigger className="bg-[hsl(var(--background))] border-[hsl(var(--border))]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    <SelectItem value="demo">{t('admin.demoBalance')}</SelectItem>
                    <SelectItem value="real">{t('admin.realBalance')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('admin.asset')}</Label>
                <Select value={balanceForm.asset} onValueChange={(v) => setBalanceForm(prev => ({ ...prev, asset: v }))}>
                  <SelectTrigger className="bg-[hsl(var(--background))] border-[hsl(var(--border))]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    <SelectItem value="usdt">USDT</SelectItem>
                    <SelectItem value="trx">TRX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('admin.amount')} (+ {t('admin.addFunds')}, - {t('admin.deductFunds')})</Label>
              <Input
                data-testid="admin-balance-amount"
                type="number"
                value={balanceForm.amount}
                onChange={(e) => setBalanceForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="100 veya -50"
                className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('admin.note')}</Label>
              <Input
                value={balanceForm.note}
                onChange={(e) => setBalanceForm(prev => ({ ...prev, note: e.target.value }))}
                placeholder={t('admin.notePlaceholder')}
                className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBalanceDialog(false)}>{t('common.cancel')}</Button>
            <Button
              data-testid="admin-balance-submit"
              disabled={balanceLoading || !balanceForm.amount}
              onClick={handleBalanceUpdate}
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2"
            >
              {balanceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('admin.updateBalance')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coin Edit Dialog */}
      <Dialog open={coinEditDialog} onOpenChange={setCoinEditDialog}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <DialogHeader>
            <DialogTitle>Coin Ayarlari: {editingCoin?.symbol}</DialogTitle>
            <DialogDescription>{editingCoin?.name} - {editingCoin?.network}</DialogDescription>
          </DialogHeader>
          {editingCoin && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Cuzdan Adresi</Label>
                <Input
                  value={editingCoin.address || ''}
                  onChange={(e) => setEditingCoin(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Platform cuzdan adresi"
                  className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Minimum Yatirma</Label>
                <Input
                  type="number"
                  value={editingCoin.min_deposit}
                  onChange={(e) => setEditingCoin(prev => ({ ...prev, min_deposit: parseFloat(e.target.value) || 0 }))}
                  className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono"
                />
              </div>
              <div>
                <Label className="text-xs">Ag (Network)</Label>
                <Input
                  value={editingCoin.network || ''}
                  onChange={(e) => setEditingCoin(prev => ({ ...prev, network: e.target.value }))}
                  className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Aktif</Label>
                <Switch
                  checked={editingCoin.enabled}
                  onCheckedChange={(v) => setEditingCoin(prev => ({ ...prev, enabled: v }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoinEditDialog(false)}>Iptal</Button>
            <Button onClick={handleCoinSave} disabled={coinSaving} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
              {coinSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
